import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Markdown renderer for chat messages using react-markdown
 * Supports full markdown features with custom styling including tables, strikethrough, and task lists
 * @return {JSX.Element} Rendered markdown content
 */
export function MarkdownRenderer({
  content,
  className = '',
}: MarkdownRendererProps) {
  // Custom components for styling markdown elements
  const components: Components = {
    // Paragraphs
    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,

    // Links
    a: ({ href, children }) => (
      <a
        href={href}
        className="text-blue-600 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),

    // Bold text
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,

    // Italic text
    em: ({ children }) => <em className="italic">{children}</em>,

    // Code block wrapper (pre tag)
    pre: ({ children }) => (
      <pre className="my-2 max-w-full overflow-x-auto rounded-lg bg-gray-900 p-3 text-gray-100">
        {children}
      </pre>
    ),

    // Inline code and code blocks
    code: ({ children }) => {
      return (
        <code className="inline-block min-w-0 font-mono text-sm whitespace-pre">
          {children}
        </code>
      );
    },

    // Headings
    h1: ({ children }) => (
      <h1 className="mt-4 mb-2 text-2xl font-bold">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="mt-3 mb-2 text-xl font-bold">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-2 mb-2 text-lg font-bold">{children}</h3>
    ),

    // Lists
    ul: ({ children }) => <ul className="mb-2 ml-4 list-disc">{children}</ul>,
    ol: ({ children }) => (
      <ol className="mb-2 ml-4 list-decimal">{children}</ol>
    ),
    li: ({ children }) => <li className="mb-1">{children}</li>,

    // Blockquote
    blockquote: ({ children }) => (
      <blockquote className="my-2 border-l-4 border-gray-300 pl-4 italic">
        {children}
      </blockquote>
    ),

    // Horizontal rule
    hr: () => <hr className="my-4 border-gray-300" />,

    // Tables
    table: ({ children }) => (
      <div className="my-2 w-full max-w-full overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr className="border-b border-gray-300">{children}</tr>
    ),
    th: ({ children }) => (
      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-300 px-4 py-2">{children}</td>
    ),

    // Strikethrough (from GFM)
    del: ({ children }) => (
      <del className="text-gray-500 line-through">{children}</del>
    ),
  };

  return (
    <div className={`markdown-content max-w-full overflow-hidden ${className}`}>
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
